/* eslint-disable @next/next/no-img-element */
import type { PurchasedProduct } from "@/hooks/usePurchasedProducts";
import { hasCustomerId } from "@/lib/api";
import { Loader2, Package, ShoppingBag, X } from "lucide-react";
import { useState } from "preact/hooks";
import { WidgetBuyAgainSheet } from "./WidgetBuyAgainSheet";
import { Logo } from "./Logo";
import { Button } from "./ui/button";

interface BuyAgainPanelProps {
  isOpen: boolean;
  onClose: () => void;
  products: PurchasedProduct[] | undefined;
  productsLoading: boolean;
}

function formatLastBought(dateStr: string | undefined): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function ProductCard({
  product,
  onBuy,
}: {
  product: PurchasedProduct;
  onBuy: (id: number) => void;
}) {
  const lastBoughtText = product.last_bought_at
    ? `Last bought ${formatLastBought(product.last_bought_at)}`
    : null;

  return (
    <div className="tw:flex tw:gap-3 tw:py-3 tw:border-b tw:border-gray-100 last:tw:border-0">
      <div className="tw:relative tw:w-16 tw:h-16 tw:bg-pink-50 tw:rounded-lg tw:shrink-0 tw:overflow-hidden">
        <img
          src={product.image_url ?? "https://socialocean.co/placeholder.png"}
          alt={product.title}
          className="tw:w-full tw:h-full tw:object-cover tw:rounded-lg"
        />
      </div>
      <div className="tw:flex tw:flex-1 tw:flex-col tw:justify-between tw:min-w-0">
        <div>
          <p className="tw:font-medium tw:text-sm tw:line-clamp-2 tw:leading-tight">
            {product.title}
          </p>
          {lastBoughtText && (
            <p className="tw:text-xs tw:text-gray-400 tw:mt-0.5">
              {lastBoughtText}
            </p>
          )}
        </div>
        <div className="tw:flex tw:items-center tw:justify-between tw:mt-1.5">
          {product.price != null && (
            <span className="tw:text-sm tw:font-semibold">
              $
              {typeof product.price === "number"
                ? product.price.toFixed(2)
                : product.price}
            </span>
          )}
          <Button
            size="xs"
            onClick={() => onBuy(product.id)}
            className="tw:text-white tw:border-0 tw:font-medium tw:px-3 tw:rounded-sm"
            style={{
              background:
                "linear-gradient(90deg, rgba(251, 187, 93, 1) 0%, rgba(252, 88, 153, 1) 100%)",
            }}
          >
            Buy
          </Button>
        </div>
      </div>
    </div>
  );
}

export function BuyAgainPanel({
  isOpen,
  onClose,
  products,
  productsLoading,
}: BuyAgainPanelProps) {
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null,
  );
  const [sheetOpen, setSheetOpen] = useState(false);

  const handleBuy = (productId: number) => {
    setSelectedProductId(productId);
    setSheetOpen(true);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="tw:fixed tw:bottom-2 tw:inset-e-2 tw:z-50 tw:w-[calc(100%-1rem)] tw:max-w-sm">
        <div className="tw:bg-white tw:rounded-xl tw:shadow-[0_4px_24px_rgba(0,0,0,0.12)] tw:border tw:border-gray-100 tw:overflow-hidden tw:flex tw:flex-col tw:max-h-[70vh]">
          {/* Header */}
          <div className="tw:flex tw:items-center tw:justify-between tw:px-4 tw:py-3 tw:border-b tw:border-gray-100">
            <div className="tw:flex tw:items-center tw:gap-2">
              <ShoppingBag className="tw:size-4 tw:text-pink-500" />
              <span className="tw:font-semibold tw:text-sm">Buy Again</span>
            </div>
            <div className="tw:flex tw:items-center tw:gap-2">
              <Logo className="tw:w-16" />
              <button
                onClick={onClose}
                className="tw:text-gray-400 hover:tw:text-gray-600 tw:cursor-pointer tw:transition-colors tw:bg-transparent tw:border-none tw:p-0"
                aria-label="Close"
              >
                <X className="tw:size-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="tw:overflow-y-auto tw:flex-1">
            {!hasCustomerId() ? (
              <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-8 tw:px-4 tw:text-center">
                <Package className="tw:size-10 tw:text-gray-300 tw:mb-3" />
                <p className="tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">
                  See your past purchases
                </p>
                <p className="tw:text-xs tw:text-gray-400">
                  Sign in to the store to quickly re-order products you&apos;ve
                  bought before.
                </p>
              </div>
            ) : productsLoading ? (
              <div className="tw:flex tw:items-center tw:justify-center tw:py-10">
                <Loader2 className="tw:size-5 tw:animate-spin tw:text-pink-500" />
              </div>
            ) : !products || products.length === 0 ? (
              <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-8 tw:px-4 tw:text-center">
                <Package className="tw:size-10 tw:text-gray-300 tw:mb-3" />
                <p className="tw:text-sm tw:font-medium tw:text-gray-700">
                  No purchases yet
                </p>
                <p className="tw:text-xs tw:text-gray-400 tw:mt-1">
                  Products you&apos;ve bought will show up here.
                </p>
              </div>
            ) : (
              <div className="tw:px-4">
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onBuy={handleBuy}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <WidgetBuyAgainSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        productId={selectedProductId}
      />
    </>
  );
}
